import React, { useState, useEffect } from "react";
import { X, Plus, Minus } from "lucide-react";

const AddExamModal = ({ onRequestClose, setExamData, isCourse, currentExamData }) => {
  console.log("Starting AddExamModal with currentExamData:", currentExamData);

  const [examDetails, setExamDetails] = useState({
    title: "",
    description: "",
    type: "MCQ", // Default to MCQ
    category: "MCQs", // Default to MCQs
    mcqs: [
      {
        question: "",
        optionA: "",
        optionB: "",
        optionC: "",
        optionD: "",
        marks: 0,
        time: 0,
        correctOption: "A" // Using single character
      }
    ],
    questionAnswers: [
      {
        question: "",
        answer: "",
        marks: 0,
        time: 0
      }
    ]
  });

  // Enhanced data initialization for editing
  useEffect(() => {
    console.log("Initializing exam data with:", currentExamData);
    
    if (currentExamData) {
      // Store the ID if it exists
      const examId = currentExamData.id;
      
      // Determine exam type - defaults to MCQ if not specified
      const examType = currentExamData.type || "MCQ";
      
      // Set category based on type
      const examCategory = examType === "MCQ" ? "MCQs" : 
                         examType === "QA" ? "QuestionAnswers" : "MCQs";
      
      // Process MCQs with consistent format
      let mcqs = [];
      if (currentExamData.mcqs && Array.isArray(currentExamData.mcqs) && currentExamData.mcqs.length > 0) {
        mcqs = currentExamData.mcqs.map(mcq => {
          // Normalize the correctOption format
          let correctOption = "A"; // Default
          
          if (mcq.correctOption) {
            if (typeof mcq.correctOption === 'string') {
              // Handle various formats like "A", "optionA", "option_a"
              if (mcq.correctOption.length === 1) {
                correctOption = mcq.correctOption.toUpperCase();
              } else if (mcq.correctOption.includes("A") || mcq.correctOption.includes("a")) {
                correctOption = "A";
              } else if (mcq.correctOption.includes("B") || mcq.correctOption.includes("b")) {
                correctOption = "B";
              } else if (mcq.correctOption.includes("C") || mcq.correctOption.includes("c")) {
                correctOption = "C";
              } else if (mcq.correctOption.includes("D") || mcq.correctOption.includes("d")) {
                correctOption = "D";
              }
            }
          }
          
          return {
            id: mcq.id, // Preserve ID for updates
            question: mcq.question || "",
            optionA: mcq.optionA || "",
            optionB: mcq.optionB || "",
            optionC: mcq.optionC || "",
            optionD: mcq.optionD || "",
            marks: typeof mcq.marks === 'number' ? mcq.marks : 0,
            time: typeof mcq.time === 'number' ? mcq.time : 0,
            correctOption: correctOption
          };
        });
      } else {
        // Default MCQ if none exists
        mcqs = [{
          question: "",
          optionA: "",
          optionB: "",
          optionC: "",
          optionD: "",
          marks: 0,
          time: 0,
          correctOption: "A"
        }];
      }
      
      // Process Question & Answers with consistent format
      let questionAnswers = [];
      if (currentExamData.questionAnswers && Array.isArray(currentExamData.questionAnswers) && 
          currentExamData.questionAnswers.length > 0) {
        questionAnswers = currentExamData.questionAnswers.map(qa => ({
          id: qa.id, // Preserve ID for updates
          question: qa.question || "",
          answer: qa.answer || "",
          marks: typeof qa.marks === 'number' ? qa.marks : 0,
          time: typeof qa.time === 'number' ? qa.time : 0
        }));
      } else {
        // Default QA if none exists
        questionAnswers = [{
          question: "",
          answer: "",
          marks: 0,
          time: 0
        }];
      }
      
      // Set the complete exam details
      const processedData = {
        id: examId, // Include ID for updates
        title: currentExamData.title || "",
        description: currentExamData.description || "",
        type: examType,
        category: examCategory,
        mcqs: mcqs,
        questionAnswers: questionAnswers
      };
      
      console.log("Processed exam data:", processedData);
      setExamDetails(processedData);
    }
  }, [currentExamData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If changing category, update the type accordingly
    if (name === "category") {
      const newType = value === "MCQs" ? "MCQ" : 
                     value === "QuestionAnswers" ? "QA" : 
                     examDetails.type;
      
      setExamDetails({ 
        ...examDetails, 
        [name]: value,
        type: newType
      });
    } else {
      setExamDetails({ ...examDetails, [name]: value });
    }
  };

  const handleMCQChange = (index, field, value) => {
    const updatedMCQs = [...examDetails.mcqs];
    updatedMCQs[index][field] = field === "marks" || field === "time" ? parseInt(value) || 0 : value;
    setExamDetails({ ...examDetails, mcqs: updatedMCQs });
  };

  const handleQAChange = (index, field, value) => {
    const updatedQAs = [...examDetails.questionAnswers];
    updatedQAs[index][field] = field === "marks" || field === "time" ? parseInt(value) || 0 : value;
    setExamDetails({ ...examDetails, questionAnswers: updatedQAs });
  };

  const addMCQ = () => {
    setExamDetails({
      ...examDetails,
      mcqs: [
        ...examDetails.mcqs,
        {
          question: "",
          optionA: "",
          optionB: "",
          optionC: "",
          optionD: "",
          marks: 0,
          time: 0,
          correctOption: "A" // Using single character
        }
      ]
    });
  };

  const removeMCQ = (index) => {
    const updatedMCQs = [...examDetails.mcqs];
    updatedMCQs.splice(index, 1);
    setExamDetails({ ...examDetails, mcqs: updatedMCQs });
  };

  const addQA = () => {
    setExamDetails({
      ...examDetails,
      questionAnswers: [
        ...examDetails.questionAnswers,
        {
          question: "",
          answer: "",
          marks: 0,
          time: 0
        }
      ]
    });
  };

  const removeQA = (index) => {
    const updatedQAs = [...examDetails.questionAnswers];
    updatedQAs.splice(index, 1);
    setExamDetails({ ...examDetails, questionAnswers: updatedQAs });
  };

  const handleSubmit = () => {
    // Validate exam details
    if (!examDetails.title.trim()) {
      alert("Please enter an exam title");
      return;
    }
  
    // Create a copy of exam details to avoid mutating the state directly
    const finalData = { ...examDetails };
    
    // Keep the ID if present (important for updates)
    if (currentExamData && currentExamData.id) {
      finalData.id = currentExamData.id;
    }
    
    // If category is MCQs, use MCQ type and preserve IDs for questionAnswers
    if (finalData.category === "MCQs") {
      finalData.type = "MCQ";
      
      // Preserve questionAnswers IDs but empty the array for API submission
      if (finalData.questionAnswers && finalData.questionAnswers.some(qa => qa.id)) {
        finalData.questionAnswers = finalData.questionAnswers
          .filter(qa => qa.id)
          .map(qa => ({ id: qa.id }));
      } else {
        finalData.questionAnswers = [];
      }
    } 
    // If category is QuestionAnswers, use QA type and preserve IDs for mcqs
    else if (finalData.category === "QuestionAnswers") {
      finalData.type = "QA";
      
      // Preserve mcqs IDs but empty the array for API submission
      if (finalData.mcqs && finalData.mcqs.some(mcq => mcq.id)) {
        finalData.mcqs = finalData.mcqs
          .filter(mcq => mcq.id)
          .map(mcq => ({ id: mcq.id }));
      } else {
        finalData.mcqs = [];
      }
    }
    
    console.log("Saving exam data:", finalData);
    
    // Pass the data to the parent component
    setExamData(finalData);
    onRequestClose();
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold">
          {currentExamData && currentExamData.id ? "Edit Exam/Quiz" : "Create Exam/Quiz"}
        </h3>
      
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block mb-1">Exam Title</label>
          <input
            type="text"
            name="title"
            value={examDetails.title}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded p-2"
            placeholder="Enter exam title"
          />
        </div>
        <div>
          <label className="block mb-1">Category</label>
          <select
            name="category"
            value={examDetails.category}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded p-2"
          >
            <option value="MCQs">MCQs</option>
            <option value="QuestionAnswers">Question & Answers</option>
          </select>
        </div>
      </div>

      <div className="mb-4">
        <label className="block mb-1">Description</label>
        <textarea
          name="description"
          value={examDetails.description}
          onChange={handleInputChange}
          className="w-full border border-gray-300 rounded p-2"
          placeholder="Enter exam description"
          rows="3"
        />
      </div>

      {/* Show MCQs section if category is MCQs */}
      {examDetails.category === "MCQs" && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-bold">Multiple Choice Questions</h4>
            <button
              type="button"
              onClick={addMCQ}
              className="bg-green-500 text-white p-1 rounded flex items-center"
            >
              <Plus size={16} /> Add MCQ
            </button>
          </div>

          {examDetails.mcqs.map((mcq, index) => (
            <div key={index} className="border p-3 rounded mb-3 bg-gray-50">
              <div className="flex justify-between items-center mb-2">
                <h5 className="font-semibold">Question {index + 1}</h5>
                {examDetails.mcqs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeMCQ(index)}
                    className="bg-red-500 text-white p-1 rounded"
                  >
                    <Minus size={16} />
                  </button>
                )}
              </div>

              <div className="mb-2">
                <textarea
                  value={mcq.question}
                  onChange={(e) => handleMCQChange(index, "question", e.target.value)}
                  className="w-full border border-gray-300 rounded p-2"
                  placeholder="Enter question"
                  rows="2"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                <div>
                  <input
                    type="text"
                    value={mcq.optionA}
                    onChange={(e) => handleMCQChange(index, "optionA", e.target.value)}
                    className="w-full border border-gray-300 rounded p-2"
                    placeholder="Option A"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={mcq.optionB}
                    onChange={(e) => handleMCQChange(index, "optionB", e.target.value)}
                    className="w-full border border-gray-300 rounded p-2"
                    placeholder="Option B"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={mcq.optionC}
                    onChange={(e) => handleMCQChange(index, "optionC", e.target.value)}
                    className="w-full border border-gray-300 rounded p-2"
                    placeholder="Option C"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={mcq.optionD}
                    onChange={(e) => handleMCQChange(index, "optionD", e.target.value)}
                    className="w-full border border-gray-300 rounded p-2"
                    placeholder="Option D"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="block text-sm">Correct Option</label>
                  <select
                    value={mcq.correctOption}
                    onChange={(e) => handleMCQChange(index, "correctOption", e.target.value)}
                    className="w-full border border-gray-300 rounded p-2"
                  >
                    <option value="A">Option A</option>
                    <option value="B">Option B</option>
                    <option value="C">Option C</option>
                    <option value="D">Option D</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm">Marks</label>
                  <input
                    type="number"
                    value={mcq.marks}
                    onChange={(e) => handleMCQChange(index, "marks", e.target.value)}
                    className="w-full border border-gray-300 rounded p-2"
                    placeholder="Marks"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm">Time (seconds)</label>
                  <input
                    type="number"
                    value={mcq.time}
                    onChange={(e) => handleMCQChange(index, "time", e.target.value)}
                    className="w-full border border-gray-300 rounded p-2"
                    placeholder="Time in seconds"
                    min="0"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Show Question & Answers section if category is QuestionAnswers */}
      {examDetails.category === "QuestionAnswers" && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-bold">Question and Answers</h4>
            <button
              type="button"
              onClick={addQA}
              className="bg-green-500 text-white p-1 rounded flex items-center"
            >
              <Plus size={16} /> Add Q&A
            </button>
          </div>

          {examDetails.questionAnswers.map((qa, index) => (
            <div key={index} className="border p-3 rounded mb-3 bg-gray-50">
              <div className="flex justify-between items-center mb-2">
                <h5 className="font-semibold">Question {index + 1}</h5>
                {examDetails.questionAnswers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeQA(index)}
                    className="bg-red-500 text-white p-1 rounded"
                  >
                    <Minus size={16} />
                  </button>
                )}
              </div>

              <div className="mb-2">
                <textarea
                  value={qa.question}
                  onChange={(e) => handleQAChange(index, "question", e.target.value)}
                  className="w-full border border-gray-300 rounded p-2"
                  placeholder="Enter question"
                  rows="2"
                />
              </div>

              <div className="mb-2">
                <textarea
                  value={qa.answer}
                  onChange={(e) => handleQAChange(index, "answer", e.target.value)}
                  className="w-full border border-gray-300 rounded p-2"
                  placeholder="Enter answer"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm">Marks</label>
                  <input
                    type="number"
                    value={qa.marks}
                    onChange={(e) => handleQAChange(index, "marks", e.target.value)}
                    className="w-full border border-gray-300 rounded p-2"
                    placeholder="Marks"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm">Time (seconds)</label>
                  <input
                    type="number"
                    value={qa.time}
                    onChange={(e) => handleQAChange(index, "time", e.target.value)}
                    className="w-full border border-gray-300 rounded p-2"
                    placeholder="Time in seconds"
                    min="0"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={handleSubmit}
          className="bg-[#241763] text-white py-2 px-4 rounded"
        >
          {currentExamData && currentExamData.id ? "Update Exam" : "Save Exam"}
        </button>
      </div>
    </div>
  );
};

export default AddExamModal;